<template>
  <div class="nx-quill-spelling-check">
    <nx-quill-spelling-error v-for="error in spellingChecksWithPositions"
                             :key="error.uuid"
                             :error="error"
                             :quill="quill"
                             :height-offset="editorDimensions.toolbarHeight"
                             @ignore="ignoreWord"
                             @replace="replaceWord"
                             @dictionary="updateWordsInDictionary"
    />

    <nx-quill-spelling-check-fab
      v-if="checkingSpellingErrors || (spellingChecks && filteredSpellingCheckErrors.length > 0)"
      :checking-spelling-errors="checkingSpellingErrors"
      :filtered-spelling-check-errors="filteredSpellingCheckErrors"
      @updateDictionary="updateWordsInDictionary"
    ></nx-quill-spelling-check-fab>

    <nx-dialog-confirm
      :title="$t('message.content.tools.spellcheck.confirmSave.title', { field: fieldLabel })"
      :text="$t('message.content.tools.spellcheck.confirmSave.text', { field: fieldLabel })"
      :cancel-text="$t('message.content.tools.spellcheck.confirmSave.cancel')"
      :accept-text="$t('message.content.tools.spellcheck.confirmSave.accept')"
      accept-color="error"
      cancel-color="primary"
      ref="confirmSave"
      width="500"
    ></nx-dialog-confirm>

    <nx-dialog-confirm
      :title="$t('message.content.tools.spellcheck.confirmSaveWithErrors.title', { field: fieldLabel })"
      :text="$t('message.content.tools.spellcheck.confirmSaveWithErrors.text', { field: fieldLabel })"
      :cancel-text="$t('message.content.tools.spellcheck.confirmSaveWithErrors.cancel')"
      :accept-text="$t('message.content.tools.spellcheck.confirmSaveWithErrors.accept')"
      accept-color="error"
      cancel-color="primary"
      ref="confirmSaveWithErrors"
      width="500"
    ></nx-dialog-confirm>

    <nx-dialog-confirm
      :title="$t('message.content.tools.spellcheck.confirmPublish.title', { field: fieldLabel })"
      :text="$t('message.content.tools.spellcheck.confirmPublish.text', { field: fieldLabel })"
      :cancel-text="$t('message.content.tools.spellcheck.confirmPublish.cancel')"
      :accept-text="$t('message.content.tools.spellcheck.confirmPublish.accept')"
      accept-color="error"
      cancel-color="primary"
      ref="confirmPublish"
      width="500"
    ></nx-dialog-confirm>

    <nx-dialog-confirm
      :title="$t('message.content.tools.spellcheck.confirmPublishWithErrors.title', { field: fieldLabel })"
      :text="$t('message.content.tools.spellcheck.confirmPublishWithErrors.text', { field: fieldLabel })"
      :cancel-text="$t('message.content.tools.spellcheck.confirmPublishWithErrors.cancel')"
      :accept-text="$t('message.content.tools.spellcheck.confirmPublishWithErrors.accept')"
      accept-color="error"
      cancel-color="primary"
      ref="confirmPublishWithErrors"
      width="500"
    ></nx-dialog-confirm>
  </div>
</template>

<script>
import Quill from 'quill';
import NxQuillSpellingError from '@/components/texteditor/spelling-check/SpellingError';
import NxQuillSpellingCheckFab from '@/components/texteditor/spelling-check/SpellingCheckFAB';
import debounce from 'lodash/debounce';
import QuillEventBus, { QuillEvents } from '@/components/texteditor/event-bus';
import api from '@/modules/texteditor/api';
import uuidV4 from 'uuid/v4';
import NxDialogConfirm from '@/components/dialogs/Confirm';
import isEmpty from 'lodash/isEmpty';
import { findKey } from 'lodash/object';
import { mapActions, mapGetters } from 'vuex';
import SpellingCheckMixin from '@/components/texteditor/mixins/spelling-check.mixin';
import toast from '@/modules/toast/services';

export default {
  name: 'nx-quill-spelling-check-tool',
  mixins: [SpellingCheckMixin],
  components: {
    NxDialogConfirm,
    NxQuillSpellingError,
    NxQuillSpellingCheckFab,
  },
  inject: {
    $getParentEditor: {
      default: () => ({}),
    },
  },
  props: {
    field: {
      type: String,
      required: true,
    },
    fieldLabel: {
      type: String,
      required: true,
    },
    quill: {
      type: Quill,
      required: true,
    },
    forceSpellcheckBeforeSave: {
      type: Boolean,
    },
    forceSpellcheckBeforePublish: {
      type: Boolean,
    },
  },
  data() {
    return {
      checkingSpellingErrors: false,
      spellingChecks: [],
      excludedWords: [],
      manualError: null,
      editorDimensions: {
        width: null,
        height: null,
        toolbarHeight: null,
      },
      executedTimes: 0,
      confirm: {
        title: null,
        text: null,
        accept: null,
        cancel: null,
      },
    };
  },
  computed: {
    ...mapGetters('staff', { personalDictionary: 'personalDictionary' }),
    filteredSpellingCheckErrors() {
      const acceptedWords = [
        ...this.excludedWords,
        ...this.personalDictionary || [],
      ].map(w => w.toLowerCase());

      return this.spellingChecks.filter((c) => {
        const errorWord = c.context.text
          .substring(c.context.offset, c.context.offset + c.context.length);

        return !acceptedWords.includes(errorWord.toLowerCase());
      });
    },
    hasSpellingCheckErrors() {
      return this.filteredSpellingCheckErrors.length > 0;
    },
    spellingChecksWithPositions() {
      // Guardamos los índices de los embed porque por cada uno de ellos genera un off-by-one error en el cálculo de índices
      // Es un bug de Quill con Embed personalizados que no parece estar solucionado en ninguna versión.
      const embedIndexes = [];
      this.quill.getLines()
        .forEach((blot) => {
          // ODMPPHE
          //
          // Existen dos embed.js en los proyectos: el de core y el de sites
          // embed.js existe en ambos sitios porque, por problemas con webpack, no se hace correctamente
          // el import una vez transpilado el JS. Esto hace que no podamos usar 'instanceof' ya que hay
          // varias clases 'Embed' y a la de sites no tenemos acceso desde core
          //
          // Los embed de Bitban tienen 3 datos (blotName, className y tagName) que nos dejan diferenciarlos de los de Quill,
          // los cuales no parecen tener el problema del off-by-one
          //
          // Si somos un embed personalizado, añadimos su índice en un array para hacer la corrección de los índices más adelante
          const protoStatics = blot.constructor.prototype.statics;
          if (protoStatics.blotName
            && protoStatics.className
            && protoStatics.tagName
          ) {
            embedIndexes.push(blot.offset());
          }
        });

      return this.filteredSpellingCheckErrors.map((check) => {
        const { offset, length } = check;

        // Si un error está antes de cualquier embed, no tiene errores off-by-one
        let offByOneCorrection = 0;

        // Por cada embed que esté antes del error tenemos que arreglar un error off-by-one
        // sumamos uno al offset por cada embed previo a nosotros
        embedIndexes.forEach((index) => {
          if (offset > index) {
            offByOneCorrection += 1;
          }
        });

        const {
          top,
          left,
          height,
          width,
        } = this.quill.getBounds(offset + offByOneCorrection, length);
        return {
          ...check,
          isManuallyOpened: this.manualError === check.uuid,
          editorDimensions: {
            width: this.editorDimensions.width,
            height: this.editorDimensions.height,
          },
          position: {
            top: Math.round(top),
            left: Math.round(left),
            height: Math.round(height),
            width: Math.round(width),
          },
          offByOneCorrection,
        };
      });
    },
  },
  methods: {
    ...mapActions(
      'staff',
      {
        getDictionary: 'GET_DICTIONARY',
        updateDictionary: 'UPDATE_DICTIONARY',
      },
    ),
    ignoreWord({ word, error }) {
      this.excludedWords = [
        ...this.excludedWords,
        word,
      ];
      this.removeErrorFromChecks(error);
    },
    replaceWord({ word, error }) {
      this.replaceInQuill(error, word);
      this.removeErrorFromChecks(error);
    },
    async updateWordsInDictionary({ words, error }) {
      this.checkingSpellingErrors = true;

      try {
        if (!words) {
          words = [];
        }
        await this.updateDictionary(words);
        if (error === undefined) {
          await this.checkSpellingErrors();
        } else {
          this.removeErrorFromChecks(error);
        }

        toast.success(this.$t('message.content.tools.spellcheck.dictionaryFeedback.ok'));
      } catch (e) {
        console.error('Error updating dictionary', e);
        toast.error(this.$t('message.content.tools.spellcheck.dictionaryFeedback.ko'));
      } finally {
        this.checkingSpellingErrors = false;
      }
    },
    removeErrorFromChecks(error) {
      this.spellingChecks = this.spellingChecks.filter(c => c !== error.uuid);
    },
    cleanAllSpellingTooltips() {
      document.querySelectorAll('.spell-check-tooltip')
        .forEach(c => c.parentNode.removeChild(c));
    },
    replaceInQuill(check, value) {
      this.quill.deleteText(check.offset + check.offByOneCorrection, check.length);
      this.quill.insertText(check.offset + check.offByOneCorrection, value);

      this.checkSpellingErrors();

      this.cleanAllSpellingTooltips();
    },
    async checkSpellingErrors() {
      try {
        this.checkingSpellingErrors = true;

        const { matches = [] } = await api.checkGrammar(this.quill.getText());
        this.spellingChecks = matches.map(check => ({
          uuid: uuidV4(),
          ...check,
        }));
      } catch (e) {
        console.error('Error while retrieving spelling checks', e);
        this.spellingChecks = [];
        toast.error(this.$t('message.content.tools.spellcheck.check.ko'));
      } finally {
        this.executedTimes += 1;
        this.checkingSpellingErrors = false;
      }
    },
    onSpellingCheck({ uuid }) {
      this.cleanAllSpellingTooltips();

      if (this.quill.uuid !== uuid) {
        return;
      }

      this.checkSpellingErrors();
    },
    async confirmSave(context) {
      const agree = await this.$refs.confirmSave.open();
      if (agree) {
        return context;
      }
      await this.checkSpellingErrors();
      context.abort = true;
      return context;
    },
    async confirmSaveWithErrors(context) {
      const agree = await this.$refs.confirmSaveWithErrors.open();
      if (agree) {
        return context;
      }
      context.abort = true;
      return context;
    },
    async confirmPublish(context) {
      const agree = await this.$refs.confirmPublish.open();
      if (agree) {
        return context;
      }
      await this.checkSpellingErrors();
      context.abort = true;
      return context;
    },
    async confirmPublishWithErrors(context) {
      const agree = await this.$refs.confirmPublishWithErrors.open();
      if (agree) {
        return context;
      }
      context.abort = true;
      return context;
    },
    fieldIsModifiedInDiff(diff) {
      return !isEmpty(diff[this.field]) || findKey(diff, this.field);
    },
    async beforeSaveCallback(context) {
      if (this.fieldIsModifiedInDiff(context.diff)
        && this.executedTimes === 0) {
        return this.confirmSave(context);
      }
      if (this.hasSpellingCheckErrors) {
        return this.confirmSaveWithErrors(context);
      }
      return context;
    },
    async beforePublishCallback(context) {
      if (this.executedTimes === 0) {
        return this.confirmPublish(context);
      }
      if (this.hasSpellingCheckErrors) {
        return this.confirmPublishWithErrors(context);
      }
      return context;
    },
    reactToContentMenuTransforms() {
      const menu = document.getElementById('nx-editor-header');
      const transformMap = {
        'translateY(0px)': 'translateY(96px)',
        'translateY(-64px)': 'translateY(30px)',
      };
      if (!menu) return;
      this.observer = new MutationObserver((mutations) => {
        if (!this.$refs.button) {
          return;
        }
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'style') {
            this.$refs.button.style.transform = transformMap[menu.style.transform];
          }
        });
      });
      this.observer.observe(menu, { attributes: true, attributeFilter: ['style'] });
    },
  },
  mounted() {
    // Resize observers
    this.containerResizeObserver = new ResizeObserver(debounce((entries) => {
      const editor = entries[0];
      this.editorDimensions.height = editor.contentRect.height;
      this.editorDimensions.width = editor.contentRect.width;
    }, 100));
    this.containerResizeObserver.observe(this.quill.container);
    this.toolbarResizeObserver = new ResizeObserver(debounce((entries) => {
      const toolbar = entries[0];
      this.editorDimensions.toolbarHeight = toolbar.target.clientHeight;
    }, 100));
    this.toolbarResizeObserver.observe(this.quill.container.previousElementSibling);

    // Observers
    QuillEventBus.$on(QuillEvents.SPELLING_CHECK, this.onSpellingCheck);
    this.quill.on(Quill.events.TEXT_CHANGE, debounce(({ ops = [] }) => {
      if (!this.hasSpellingCheckErrors) {
        return;
      }
      if (isEmpty(ops)) {
        return;
      }

      let start = 0;
      let end = 0;
      ops.forEach((entry) => {
        const operation = Object.keys(entry)[0];
        const value = entry[operation];

        switch (operation) {
          case 'retain':
            start = value;
            break;
          case 'delete':
            end = value;
            break;
          case 'insert':
            end = start + value.length;
            break;
          default:
            end = value;
        }
      });

      if (end > 0) {
        this.cleanAllSpellingTooltips();
        this.checkSpellingErrors();
      }
    }, 600));

    // Before save/publish hooks
    if (this.forceSpellcheckBeforeSave) {
      this.$getParentEditor().dispatch('ADD_BEFORE_SAVE_CALLBACK', this.beforeSaveCallback);
    }
    if (this.forceSpellcheckBeforePublish) {
      this.$getParentEditor().dispatch('ADD_BEFORE_PUBLISH_CALLBACK', this.beforePublishCallback);
    }

    this.reactToContentMenuTransforms();

    // Get dictionary ONLY if it hasn't been already got
    if (this.personalDictionary === null) {
      this.getDictionary();
    }
  },
  beforeDestroy() {
    this.containerResizeObserver.disconnect();
    this.toolbarResizeObserver.disconnect();
    this.observer.disconnect();
    QuillEventBus.$off(QuillEvents.SPELLING_CHECK);
  },
};
</script>
